import { useEffect, useMemo } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { Wizard, WizardStep, WizardHeaderWithOverview } from 'foundations-components/transitional/components/Wizard';
import { useFetchUsersByScope } from '../../hooks/useFetchUsersByScope';
import { useEditApproval } from '../../hooks/useEditApproval';
import { ApprovalEditPanelStepOne } from './ApprovalEditPanelStepOne';
import { ApprovalEditPanelNextButtonTooltip } from '../i18n/ApprovalEditPanelNextButtonTooltip';
import { ApprovalEditPanelReview as ApprovalEditPanelStepTwo } from './ApprovalEditPanelStepTwo';
import { renderIfEditable } from '../../hoc/renderIfEditable';
import { updateApproversSuccessAlert } from '../../utils/alertUtils';
import { floatingErrorAlert } from '../../utils/alertUtils';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { getErrorSubCategoryFromError } from '../../utils/errorUtils';
import { trackInteraction } from '../../utils/usageTracker';
import { APPROVAL_EDIT_PANEL_CANCELLED, APPROVAL_EDIT_PANEL_SAVED } from '../../constants/trackingConstants';
import { withErrorBoundary } from '../../hoc/withErrorBoundary';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ApprovalEditPanel = ({
  approvalAction,
  approvalType,
  onClose
}) => {
  const approval = useApprovalContext();
  const {
    approvedApproverOwnerIds,
    approvedApproverUserIds,
    approversAll,
    setSelectedApproverIdsMutable,
    isFetchApproversError,
    isLoading,
    numberOfChanges,
    selectedApproverIdsMutable,
    validApproverOwnerIds
  } = useEditApproval(approvalType, approvalAction);
  const {
    users: superAdminUsers
  } = useFetchUsersByScope('super-admin');
  const superAdminUserIds = useMemo(() => new Set(superAdminUsers.map(user => user.id)), [superAdminUsers]);
  const alertId = 'edit-panel-max-approvers-alert';
  useEffect(() => {
    if (selectedApproverIdsMutable.length > 10) {
      FloatingAlertStore.addAlert({
        id: alertId,
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalEditPanel.errorAlertExceedMaxApprovers.title"
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalEditPanel.errorAlertExceedMaxApprovers.message"
        }),
        type: 'danger'
      });
    } else {
      FloatingAlertStore.removeAlert(alertId);
    }
  }, [selectedApproverIdsMutable]);
  const changeSelectValue = ({
    target: {
      value
    }
  }) => {
    setSelectedApproverIdsMutable(value);
  };
  const changeCheckbox = approverId => {
    setSelectedApproverIdsMutable(prevState => {
      if (prevState.includes(approverId)) {
        return prevState.filter(id => id !== approverId);
      } else {
        return [...prevState, approverId];
      }
    });
  };
  const hasSelectedInvalidApprovers = useMemo(() => !isFetchApproversError && approversAll.some(approver => selectedApproverIdsMutable.includes(approver.ownerId) && !validApproverOwnerIds.has(approver.ownerId) && !approvedApproverOwnerIds.includes(approver.ownerId)), [isFetchApproversError, approversAll, selectedApproverIdsMutable, validApproverOwnerIds, approvedApproverOwnerIds]);
  const isPrimaryButtonDisabled = isFetchApproversError || numberOfChanges < 1 || selectedApproverIdsMutable.length > 10 || selectedApproverIdsMutable.length < 1 || hasSelectedInvalidApprovers;
  const confirmEditApprovalUpdate = () => {
    const extra = {
      approvalId: approval.data.id
    };
    approval.api.update({
      approverOwnerIds: selectedApproverIdsMutable
    }).then(() => {
      onClose();
      updateApproversSuccessAlert();
      trackInteraction(APPROVAL_EDIT_PANEL_SAVED, approval.meta.approvalType, extra);
    }).catch(errorResponse => {
      const errorSubCategory = getErrorSubCategoryFromError(errorResponse);
      floatingErrorAlert(approval.meta.approvalType, errorSubCategory);
      console.error('Failed to update approval.');
    });
  };
  const cancelEdit = () => {
    const extra = {
      approvalId: approval.data.id
    };
    trackInteraction(APPROVAL_EDIT_PANEL_CANCELLED, approval.meta.approvalType, extra);
    onClose();
  };
  return /*#__PURE__*/_jsx(ModalPanel, {
    "data-test-id": "product-approvals-edit-approvers-panel",
    children: /*#__PURE__*/_jsxs(Wizard, {
      cancellable: true,
      disablePrimaryButton: isPrimaryButtonDisabled,
      defaultDoneLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.save"
      }),
      defaultHeading: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.reassignApprovers"
      }),
      headerComponent: WizardHeaderWithOverview,
      primaryButtonTooltip: isPrimaryButtonDisabled && /*#__PURE__*/_jsx(ApprovalEditPanelNextButtonTooltip, {
        hasSelectedInvalidApprovers: hasSelectedInvalidApprovers,
        isFetchApproversError: isFetchApproversError,
        numberOfApprovers: selectedApproverIdsMutable.length,
        numberOfChanges: numberOfChanges
      }),
      onConfirm: confirmEditApprovalUpdate,
      onReject: cancelEdit,
      children: [/*#__PURE__*/_jsx(WizardStep, {
        name: I18n.text('productApprovals.chooseApprovers'),
        children: /*#__PURE__*/_jsx(ApprovalEditPanelStepOne, {
          approvedApproverUserIds: approvedApproverUserIds,
          approversAll: approversAll,
          changeCheckbox: changeCheckbox,
          changeSelectValue: changeSelectValue,
          isFetchApproversError: isFetchApproversError,
          isLoading: isLoading,
          selectedApproverIdsMutable: selectedApproverIdsMutable,
          superAdminUserIds: superAdminUserIds,
          validApproverOwnerIds: validApproverOwnerIds
        })
      }), /*#__PURE__*/_jsx(WizardStep, {
        name: I18n.text('productApprovals.review'),
        children: /*#__PURE__*/_jsx(ApprovalEditPanelStepTwo, {
          approvedApproverOwnerIds: approvedApproverOwnerIds,
          approversAll: approversAll,
          assetName: approval.meta.assetName,
          selectedApproverIdsMutable: selectedApproverIdsMutable,
          superAdminUserIds: superAdminUserIds
        })
      })]
    })
  });
};
const ApprovalEditPanelWithErrorBoundary = withErrorBoundary(renderIfEditable(ApprovalEditPanel), 'ApprovalEditPanel');
export { ApprovalEditPanelWithErrorBoundary as ApprovalEditPanel };